import {readCookie} from "@khan/data-cookies";
import {khanFetch} from "@khan/data-fetch";
import {resetClientStores} from "@khan/data-gql-apollo";
import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

/**
 * Determine if the page is currently not visible to the user.
 */
export const isPageHidden = (): boolean => document.hidden;

/**
 * Get the user's KAAC cookie, removing the signature and timestamp.
 * In practice we only care if the user's details have changed. The best
 * way to do that is to remove the signature and timestamp from the front
 * of the KAAC and just use the rest for comparison. We don't need to
 * decode the values, encoded is just fine.
 *
 * See `shared/auth/tokens.py` and `web/request/auth/cookies.py` for more
 * details about which fields are available and how they're formatted.
 */
export const getKAAC = (): string | null | undefined => {
    // Read the KAAC cookie
    let KAAC = readCookie("KAAC");

    // If the cookie isn't defined then we just return that
    if (!KAAC) {
        return KAAC;
    }

    // Remove the signature
    KAAC = KAAC.replace(/^[^.]*\./, "");

    // Remove the timestamp
    // % is used by v1 KAAC
    // ~ is used by v2 KAAC
    return KAAC.replace(/^[~%][^$]*/, "");
};

const getLatestStaticVersion = (): Promise<string> =>
    khanFetch("/_api/static_version")
        .then((response) => response.json())
        .then((json) => json.version);

const getLatestContentVersion = (kaLocale: string): Promise<string> =>
    khanFetch(`/_fastly/published-content-version/${kaLocale}`)
        .then((response) => response.json())
        .then((json) => json.publishedContentVersion);

/**
 * Run a query getting the latest publish commit and static version
 * These pieces of information influence if we should be forcing the
 * page to refresh or clearing our internal datastores.
 */
export const getLatestVersions = async (): Promise<{
    currentStaticVersion: string;
    currentCommitSHA: string;
}> => {
    try {
        const [currentStaticVersion, currentCommitSHA] = await Promise.all([
            getLatestStaticVersion(),
            getLatestContentVersion(RequestInfo.kaLocale),
        ]);
        return {currentStaticVersion, currentCommitSHA};
    } catch (e: any) {
        throw new KAError(
            "Incomplete commit and static version.",
            Errors.Internal,
        );
    }
};

/**
 * Utility for deleting all of the most important cached data we have (both
 * from the Apollo cache and from the content data cache).
 *
 * This will make it so that future navigations to previously-visited content
 * will reload with the latest content from the server (which will then be
 * cached again). Apollo-cached data will be cleared and in-flight requests
 * will be restarted.
 *
 * We dynamically load the imports here to avoid loading modules that we will
 * only need to execute occasionally.
 */
export const clearDataCaches = (): Promise<void> =>
    Promise.all([
        import("../../content-library-legacy/content-data-request-handler.ts"),
    ]).then(([{clearContentDataCache}]: [any]) => {
        // Clear out the content data cache, causing all future requests for
        // content data to be re-requested (and cached again)
        clearContentDataCache();

        // Clear the Apollo Cache and restart all queries that
        // are currently in flight
        resetClientStores();
    });
